import { useState, useEffect } from "react";
import Button from "../../atoms/Button";
import PromoImageUploader from "../../atoms/ImageUploaders/PromoImageUploader";
import { useDispatch, useSelector } from "react-redux";
import { IMAGE_TYPES } from "../../../helpers/const";
import {
  saveAdminImage,
  updateAdminImage,
  updateSiteProperties,
  removeAdminImage,
} from "../../../store/slices/AdminSlice/AdminSlice";
import DisplayImage from "../../atoms/ImageUploaders/ImageDisplay";
import ColorPicker from "../../atoms/ColorPicker/ColorPicker";
import { updateFilter } from "../../../store/slices/ProductFinder/ProductFinderSlice";

export default function SiteAdmin() {
  const [openImageUpload, setOpenImageUpload] = useState(false);
  const [imageParams, setOpenImageParams] = useState({});
  const [title, setTitle] = useState("");
  const [currentImageType, setCurrentImageType] = useState(
    IMAGE_TYPES.PROMO_IMAGE
  );
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [approvalConfirmText, setApprovalConfirmText] = useState("");
  const [pendingApprovalValue, setPendingApprovalValue] = useState(null);
  const [promoSearch, setPromoSearch] = useState("");
  const [promoFilter, setPromoFilter] = useState("all");
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);
  const { filterObject } = useSelector((state) => state.productFinder);
  const {
    allPromoImages: promoImages,
    titleImage,
    logoImage,
    backgroundImage,
    title: reduxTitle,
    allowApproval: allow_approval,
  } = useSelector((state) => state.admin);

  // Initialize title from Redux when component loads
  useEffect(() => {
    if (reduxTitle && !title) {
      setTitle(reduxTitle);
    }
  }, [reduxTitle, title]);

  //color managers
  const [newPrimary, setNewPrimary] = useState(
    getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-color")
      .trim()
  );
  const [newSecondary, setNewSecondary] = useState(
    getComputedStyle(document.documentElement)
      .getPropertyValue("--secondary-color")
      .trim()
  );

  // Update colors dynamically when CSS properties change
  useEffect(() => {
    const updateColors = () => {
      const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--primary-color")
        .trim();
      const secondaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--secondary-color")
        .trim();

      if (primaryColor && primaryColor !== newPrimary) {
        setNewPrimary(primaryColor);
      }
      if (secondaryColor && secondaryColor !== newSecondary) {
        setNewSecondary(secondaryColor);
      }
    };

    // Update immediately
    updateColors();

    // Create a MutationObserver to watch for changes to the document's style attribute
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // Also listen for any potential style changes via other means
    const intervalId = setInterval(updateColors, 1000);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, [newPrimary, newSecondary]);

  const applySiteSettings = () => {
    console.log(
      "applySiteSettings - allow_approval from Redux:",
      allow_approval
    );
    const payload = {
      newPrimary,
      newSecondary,
      title,
      allow_approval: allow_approval || "N", // Default to 'N' if undefined
    };
    console.log("applySiteSettings - payload:", payload);
    dispatch(updateSiteProperties(payload));
  };

  const handleApprovalToggle = (newValue) => {
    setPendingApprovalValue(newValue);
    setShowApprovalConfirm(true);
    setApprovalConfirmText("");
  };

  const confirmApprovalToggle = () => {
    if (approvalConfirmText.toLowerCase() === "confirm") {
      console.log(
        "confirmApprovalToggle - pendingApprovalValue:",
        pendingApprovalValue
      );
      const payload = {
        newPrimary,
        newSecondary,
        title,
        allow_approval: pendingApprovalValue,
      };
      console.log("confirmApprovalToggle - payload:", payload);
      dispatch(updateSiteProperties(payload));
      setShowApprovalConfirm(false);
      setApprovalConfirmText("");
      setPendingApprovalValue(null);
    }
  };

  const cancelApprovalToggle = () => {
    setShowApprovalConfirm(false);
    setApprovalConfirmText("");
    setPendingApprovalValue(null);
  };

  const onEditImage = (params, imageType = IMAGE_TYPES.PROMO_IMAGE) => {
    setOpenImageParams(params || {});
    setCurrentImageType(imageType);
    setOpenImageUpload(true);
  };

  const openImageUploadModal = (imageType) => {
    console.log("openImageUploadModal - imageType:", imageType);
    console.log("openImageUploadModal - IMAGE_TYPES:", IMAGE_TYPES);
    setCurrentImageType(imageType);
    setOpenImageParams({});
    setOpenImageUpload(true);
  };

  const dispatch = useDispatch();
  const submitImage = ({
    image,
    label,
    start_date,
    end_date,
    customer_num,
    is_parent_number,
    is_aftermarket_number,
  }) => {
    console.log("SiteAdmin submitImage - currentImageType:", currentImageType);
    console.log("SiteAdmin submitImage - IMAGE_TYPES:", IMAGE_TYPES);
    const isPromo = currentImageType === IMAGE_TYPES.PROMO_IMAGE;
    const payload = {
      image,
      label: isPromo ? label : undefined,
      start_date,
      end_date,
      filterObject: isPromo ? filterObject : undefined,
      image_type: currentImageType,
      customer_num: isPromo ? customer_num : undefined,
      is_parent_number: isPromo ? is_parent_number : undefined,
      is_aftermarket_number: isPromo ? is_aftermarket_number : undefined,
    };
    console.log("SiteAdmin submitImage - payload:", payload);
    dispatch(saveAdminImage(payload));
    setOpenImageUpload(false);
  };

  const updateImage = ({
    image,
    label,
    start_date,
    end_date,
    id,
    customer_num,
    is_parent_number,
    is_aftermarket_number,
  }) => {
    console.log("SiteAdmin updateImage - currentImageType:", currentImageType);
    console.log("SiteAdmin updateImage - IMAGE_TYPES:", IMAGE_TYPES);
    const isPromo = currentImageType === IMAGE_TYPES.PROMO_IMAGE;
    const payload = {
      image,
      label: isPromo ? label : undefined,
      start_date,
      end_date,
      filterObject: isPromo ? filterObject : undefined,
      image_type: currentImageType,
      id,
      customer_num: isPromo ? customer_num : undefined,
      is_parent_number: isPromo ? is_parent_number : undefined,
      is_aftermarket_number: isPromo ? is_aftermarket_number : undefined,
    };
    console.log("SiteAdmin updateImage - payload:", payload);
    dispatch(updateAdminImage(payload));
    setOpenImageUpload(false);
  };

  const renderImageSection = (images, title, imageType, emptyMessage) => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-lg">{title}</h3>
        <Button
          textContent={`Add ${title}`}
          className="!bg-[var(--primary-color)] hover:!bg-[var(--secondary-color)] !text-sm !py-1 !px-3"
          onClick={() => openImageUploadModal(imageType)}
        />
      </div>
      {images && images.length > 0 ? (
        images.map((img) => (
          <DisplayImage
            key={img.id}
            src={img.image_url}
            id={img.id}
            label={img.label}
            onEdit={(params) =>
              onEditImage(
                {
                  ...params,
                  customerNumInit: img.customer_num ?? "",
                  isParentNumberInit: img.is_parent_number ?? false,
                  isAftermarketNumberInit: img.is_aftermarket_number ?? false,
                },
                imageType
              )
            }
            action={img.search_string}
            startDate={img.start_date}
            endDate={img.end_date}
          />
        ))
      ) : (
        <div className="text-center text-gray-500 italic border border-dashed border-gray-300 p-4 rounded-md bg-gray-50 text-sm">
          {emptyMessage}
        </div>
      )}
    </div>
  );

  const renderSingleImageSection = (image, title, imageType, emptyMessage) => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-lg">{title}</h3>
        {!image && (
          <Button
            textContent={`Add ${title}`}
            className="!bg-[var(--primary-color)] hover:!bg-[var(--secondary-color)] !text-sm !py-1 !px-3"
            onClick={() => openImageUploadModal(imageType)}
          />
        )}
      </div>
      {image ? (
        <DisplayImage
          key={image.id}
          src={image.image_url}
          id={image.id}
          label={image.label}
          onEdit={(params) => onEditImage(params, imageType)}
          action={image.search_string}
          startDate={image.start_date}
          endDate={image.end_date}
        />
      ) : (
        <div className="text-center text-gray-500 italic border border-dashed border-gray-300 p-4 rounded-md bg-gray-50 text-sm">
          {emptyMessage}
        </div>
      )}
    </div>
  );

  return (
    <>
      {openImageUpload && (
        <PromoImageUploader
          isOpen={openImageUpload}
          onClose={() => setOpenImageUpload(false)}
          submit={submitImage}
          update={updateImage}
          hideLabel={currentImageType !== IMAGE_TYPES.PROMO_IMAGE}
          hideFilter={currentImageType !== IMAGE_TYPES.PROMO_IMAGE}
          hideCustomer={currentImageType !== IMAGE_TYPES.PROMO_IMAGE}
          title={
            currentImageType === IMAGE_TYPES.TITLE_IMAGE
              ? "Upload Title Image"
              : currentImageType === IMAGE_TYPES.LOGO_IMAGE
                ? "Upload Logo Image"
                : currentImageType === IMAGE_TYPES.BACKGROUND_IMAGE
                  ? "Upload Background Image"
                  : "Upload Promo Image"
          }
          {...imageParams}
        />
      )}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left column: Images */}
        <div className="flex flex-col">
          <h2 className="font-semibold border-b text-2xl mb-4">Images</h2>

          {/* Title Images */}
          {renderSingleImageSection(
            titleImage,
            "Title Image",
            IMAGE_TYPES.TITLE_IMAGE,
            "No title image uploaded!"
          )}

          {/* Logo Images */}
          {renderSingleImageSection(
            logoImage,
            "Logo Image",
            IMAGE_TYPES.LOGO_IMAGE,
            "No logo image uploaded!"
          )}

          {/* Background Images */}
          {renderSingleImageSection(
            backgroundImage,
            "Background Image",
            IMAGE_TYPES.BACKGROUND_IMAGE,
            "No background image uploaded!"
          )}

          {/* Promo Images */}
          {(() => {
            const FILTER_OPTS = [
              { key: "all", label: "All" },
              { key: "global", label: "Global" },
              { key: "customer", label: "Customer" },
              { key: "group", label: "Group" },
              { key: "network", label: "Network" },
            ];

            const getTargeting = (img) => {
              if (!img.customer_num)
                return { label: "All Customers", cls: "bg-green-100 text-green-800" };
              if (img.is_parent_number)
                return { label: `Group: ${img.customer_num}`, cls: "bg-purple-100 text-purple-800" };
              if (img.is_aftermarket_number)
                return { label: `Network: ${img.customer_num}`, cls: "bg-orange-100 text-orange-800" };
              return { label: `Customer ${img.customer_num}`, cls: "bg-blue-100 text-blue-800" };
            };

            const filtered = (promoImages || []).filter((img) => {
              const q = promoSearch.toLowerCase();
              const matchesSearch =
                !q ||
                (img.label || "").toLowerCase().includes(q) ||
                String(img.customer_num ?? "").includes(q);
              const matchesFilter =
                promoFilter === "all" ||
                (promoFilter === "global" && !img.customer_num) ||
                (promoFilter === "customer" && img.customer_num && !img.is_parent_number && !img.is_aftermarket_number) ||
                (promoFilter === "group" && img.is_parent_number) ||
                (promoFilter === "network" && img.is_aftermarket_number);
              return matchesSearch && matchesFilter;
            });

            return (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-lg">
                    Promo Images{" "}
                    <span className="text-sm font-normal text-gray-400">
                      ({filtered.length}/{(promoImages || []).length})
                    </span>
                  </h3>
                  <Button
                    textContent="Add Promo Image"
                    className="!bg-[var(--primary-color)] hover:!bg-[var(--secondary-color)] !text-sm !py-1 !px-3"
                    onClick={() => openImageUploadModal(IMAGE_TYPES.PROMO_IMAGE)}
                  />
                </div>

                {/* Search + filter */}
                <div className="mb-3 flex flex-col gap-2">
                  <input
                    type="text"
                    value={promoSearch}
                    onChange={(e) => setPromoSearch(e.target.value)}
                    placeholder="Search by label or customer #…"
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                  />
                  <div className="flex gap-1 flex-wrap">
                    {FILTER_OPTS.map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setPromoFilter(key)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          promoFilter === key
                            ? "bg-[var(--primary-color)] text-white border-[var(--primary-color)]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[var(--primary-color)]"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image rows */}
                {filtered.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {filtered.map((img) => {
                      const targeting = getTargeting(img);
                      const isConfirming = confirmingRemoveId === img.id;
                      return (
                        <div
                          key={img.id}
                          className="flex items-center gap-3 p-3 bg-white shadow rounded-xl"
                        >
                          <span className="text-xs font-mono text-gray-400 w-8 shrink-0">
                            #{img.id}
                          </span>
                          <img
                            src={img.image_url}
                            alt={img.label}
                            className="h-14 w-14 object-cover rounded border border-gray-200 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">
                              {img.label || <span className="italic text-gray-400">No label</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${targeting.cls}`}>
                                {targeting.label}
                              </span>
                              <span className="text-xs text-gray-400">
                                {img.start_date} – {img.end_date}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() =>
                                onEditImage(
                                  {
                                    imageUrl: img.image_url,
                                    labelInit: img.label,
                                    startDateInit: img.start_date,
                                    endDateInit: img.end_date,
                                    searchString: img.search_string,
                                    id: img.id,
                                    customerNumInit: img.customer_num ?? "",
                                    isParentNumberInit: img.is_parent_number ?? false,
                                    isAftermarketNumberInit: img.is_aftermarket_number ?? false,
                                  },
                                  IMAGE_TYPES.PROMO_IMAGE
                                )
                              }
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            {isConfirming ? (
                              <>
                                <button
                                  onClick={() => {
                                    dispatch(removeAdminImage({ id: img.id }));
                                    setConfirmingRemoveId(null);
                                  }}
                                  className="text-red-600 font-medium text-sm hover:underline"
                                >
                                  Sure?
                                </button>
                                <button
                                  onClick={() => setConfirmingRemoveId(null)}
                                  className="text-gray-400 text-sm hover:underline"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setConfirmingRemoveId(img.id)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 italic border border-dashed border-gray-300 p-4 rounded-md bg-gray-50 text-sm">
                    {(promoImages || []).length === 0
                      ? "No promo images currently saved!"
                      : "No images match your search."}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Right column: Design */}
        <div className="flex flex-col">
          <h2 className="font-semibold border-b text-2xl mb-4">Design</h2>

          {/* Site Title */}
          <div className="mb-4">
            <label className="block font-medium mb-2">Site Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter site title"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <ColorPicker
            value={newPrimary}
            setValue={setNewPrimary}
            label="Primary Color"
          />
          <div className="mt-4">
            <ColorPicker
              value={newSecondary}
              setValue={setNewSecondary}
              label="Secondary Color"
            />
          </div>

          {/* Allow Approval Setting */}
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <label className="block font-medium">Allow Approval</label>
              {/* Status Badge */}
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                  allow_approval === "Y"
                    ? "bg-green-100 text-green-800 animate-pulse"
                    : "bg-red-100 text-red-800"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    allow_approval === "Y" ? "bg-green-500" : "bg-red-500"
                  }`}
                ></div>
                {allow_approval === "Y" ? "ENABLED" : "DISABLED"}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Current Status:{" "}
                <strong>
                  {allow_approval === "Y" ? "Enabled" : "Disabled"}
                </strong>
              </span>
              <div className="flex gap-2">
                <Button
                  textContent={
                    "Click to " +
                    (allow_approval === "Y" ? "Disable" : "Enable")
                  }
                  className={`!text-sm !py-1 !px-3 ${
                    allow_approval === "Y"
                      ? "!bg-red-500 hover:!bg-red-600 !text-white"
                      : "!bg-green-500 hover:!bg-green-600 !text-white"
                  }`}
                  onClick={() =>
                    handleApprovalToggle(allow_approval === "Y" ? "N" : "Y")
                  }
                />
              </div>
            </div>
          </div>

          {/* Confirmation Modal */}
          {showApprovalConfirm && (
            <div className="mt-4 p-4 border border-orange-300 bg-orange-50 rounded-md">
              <div className="mb-2 text-orange-800 font-semibold">
                Are you sure you want to{" "}
                {pendingApprovalValue === "Y" ? "enable" : "disable"} automatic
                order approval?
              </div>
              <div className="mb-3 text-sm text-orange-700">
                Type{" "}
                <span className="font-mono bg-white border px-1">confirm</span>{" "}
                to proceed:
              </div>
              <input
                type="text"
                value={approvalConfirmText}
                onChange={(e) => setApprovalConfirmText(e.target.value)}
                className="border rounded px-2 py-1 mr-2 w-full mb-2"
                placeholder="Type confirm to proceed"
              />
              <div className="flex gap-2">
                <Button
                  textContent="Confirm"
                  className="!bg-orange-600 hover:!bg-orange-700 !text-white !text-sm !py-1 !px-3"
                  disabled={approvalConfirmText.toLowerCase() !== "confirm"}
                  onClick={confirmApprovalToggle}
                />
                <Button
                  textContent="Cancel"
                  className="!bg-gray-300 hover:!bg-gray-400 !text-gray-800 !text-sm !py-1 !px-3"
                  onClick={cancelApprovalToggle}
                />
              </div>
            </div>
          )}

          <div className="mt-6">
            <Button
              textContent="Apply Settings"
              className="!bg-[var(--primary-color)] hover:!bg-[var(--secondary-color)]"
              onClick={applySiteSettings}
            />
          </div>
        </div>
      </div>
    </>
  );
}
